"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  clearMyBooks,
  loadMyBooks,
  saveMyBooks,
} from "@/lib/actions/my-books-preferences";
import { useSportsbookPreferences } from "@/contexts/sportsbook-preferences-context";
import { useBookAvailability } from "@/contexts/book-availability-context";
import { keepHidden } from "@/lib/book-location";
import {
  SPORTSBOOK_SURFACE,
  getProfileBookIdsForSurface,
  getSurfaceDefaultBookIds,
  getSportsbookSurfacePolicy,
} from "@/lib/sportsbook-preferences";
import { normalizeSportsbookId } from "@/lib/sportsbook-registry";
import { LOCAL_BYPASS_USER_ID } from "@/lib/local-bypass-auth";
import { phCapture } from "@/utils/analytics";

export interface UseMyBooksReturn {
  myBooks: string[];
  enabledBookIds: string[];
  listedBookIds: string[];
  globalDefaultBookIds: string[];
  selectableBookIds: readonly string[];
  savedBookIds: readonly string[];
  hasSavedBooks: boolean;
  usingSavedBooks: boolean;
  applySavedBooks: () => void;
  toggleBook: (bookId: string) => void;
  setMany: (bookIds: readonly string[], enabled: boolean) => Promise<boolean>;
  isBookEnabled: (bookId: string) => boolean;
  clearAll: () => Promise<boolean>;
  selectAll: () => void;
  disableAll: (bookIds: string[]) => void;
  setSelection: (bookIds: readonly string[]) => Promise<boolean>;
  hasCustomSelection: boolean;
  shouldFilterBooks: boolean;
  isConfigured: boolean;
  isOverrideActive: boolean;
  error: string | null;
}

export function useMyBooks(
  userId?: string,
  initialDisabledBooks?: string[] | null,
): UseMyBooksReturn {
  const { state: preferenceState, actions, meta } = useSportsbookPreferences();
  const { isHere } = useBookAvailability();
  const [legacyDisabledBooks, setLegacyDisabledBooks] = useState<string[]>(
    () => initialDisabledBooks ?? [],
  );
  const [legacySaving, setLegacySaving] = useState(false);

  const accountIdRef = useRef<string | undefined>(userId);
  const loadSeqRef = useRef(0);

  useEffect(() => {
    if (userId === accountIdRef.current) return;
    accountIdRef.current = userId;
    const seq = ++loadSeqRef.current;
    setLegacyDisabledBooks([]);

    if (!userId) return;
    void loadMyBooks()
      .then((result) => {
        if (seq !== loadSeqRef.current || accountIdRef.current !== userId) return;
        setLegacyDisabledBooks(result ?? []);
      })
      .catch((error) => {
        if (seq !== loadSeqRef.current || accountIdRef.current !== userId) return;
        console.error("[MyBooks] Failed to load legacy preferences:", error);
      });
  }, [userId]);

  const localOnly = userId === LOCAL_BYPASS_USER_ID;
  const preferencesReady = meta.status === "ready" || localOnly;
  const isConfigured = meta.status === "ready" && meta.isConfigured;
  const supportedBookIds = getSportsbookSurfacePolicy(
    SPORTSBOOK_SURFACE.ODDS_SCREEN,
  ).supportedBookIds;
  const effectiveBookIds = useMemo(
    () => isConfigured
      ? meta.getEffectiveBookIds(SPORTSBOOK_SURFACE.ODDS_SCREEN)
      : [],
    [isConfigured, meta],
  );
  const effectiveBookSet = useMemo(
    () => new Set<string>(effectiveBookIds),
    [effectiveBookIds],
  );
  const globalDefaultBookIds = useMemo(
    () => isConfigured
      ? getSurfaceDefaultBookIds(
          preferenceState.profile,
          SPORTSBOOK_SURFACE.ODDS_SCREEN,
        )
      : [],
    [isConfigured, preferenceState.profile],
  );
  const isOverrideActive = isConfigured
    && meta.isOverrideActive(SPORTSBOOK_SURFACE.ODDS_SCREEN);
  const hasSavedBooks = isConfigured && preferenceState.profile?.mode === "custom";
  const hasCustomSelection = isConfigured
    ? isOverrideActive
    : legacyDisabledBooks.length > 0;
  const selectableBookIds = isConfigured ? globalDefaultBookIds : supportedBookIds;
  const savedBookIds = useMemo(
    () => (isConfigured ? getProfileBookIdsForSurface(preferenceState.profile, SPORTSBOOK_SURFACE.ODDS_SCREEN) : supportedBookIds),
    [isConfigured, preferenceState.profile, supportedBookIds],
  );

  const disabledBooks = useMemo(
    () => isConfigured
      ? supportedBookIds.filter((bookId) => !effectiveBookSet.has(bookId))
      : legacyDisabledBooks,
    [effectiveBookSet, isConfigured, legacyDisabledBooks, supportedBookIds],
  );
  const rawEnabledBookIds = useMemo(
    () => supportedBookIds.filter((bookId) => isConfigured
      ? effectiveBookSet.has(bookId)
      : !legacyDisabledBooks.includes(bookId)),
    [effectiveBookSet, isConfigured, legacyDisabledBooks, supportedBookIds],
  );
  const enabledBookIds = useMemo(
    () => rawEnabledBookIds.filter(isHere),
    [isHere, rawEnabledBookIds],
  );

  const saveLegacyDenylist = useCallback(async (
    previous: string[],
    next: string[],
  ): Promise<boolean> => {
    if (!preferencesReady || legacySaving) return false;
    const account = accountIdRef.current;
    setLegacyDisabledBooks(next);
    if (localOnly) return true;
    setLegacySaving(true);
    try {
      const result = next.length === 0
        ? await clearMyBooks()
        : await saveMyBooks(next);
      if (!result.success) {
        if (accountIdRef.current === account) setLegacyDisabledBooks(previous);
        console.error("[MyBooks] Failed to persist:", result.error);
        return false;
      }
      return true;
    } catch (error) {
      if (accountIdRef.current === account) setLegacyDisabledBooks(previous);
      console.error("[MyBooks] Failed to persist:", error);
      return false;
    } finally {
      setLegacySaving(false);
    }
  }, [legacySaving, localOnly, preferencesReady]);

  const saveCanonicalSelection = useCallback(async (
    bookIds: readonly string[],
  ): Promise<boolean> => {
    if (!isConfigured) return false;
    const result = await actions.saveMultiSelection(
      SPORTSBOOK_SURFACE.ODDS_SCREEN,
      bookIds,
    );
    return result.success;
  }, [actions, isConfigured]);

  const normalizeSelection = useCallback((bookIds: readonly string[]) => {
    const selectedSet = new Set(
      bookIds
        .map((bookId) => normalizeSportsbookId(bookId))
        .filter((bookId): bookId is NonNullable<typeof bookId> => Boolean(bookId)),
    );
    return selectableBookIds.filter((bookId) => selectedSet.has(bookId));
  }, [selectableBookIds]);

  const setSelection = useCallback(async (
    bookIds: readonly string[],
  ): Promise<boolean> => {
    if (!preferencesReady) return false;
    const next = keepHidden(normalizeSelection(bookIds), rawEnabledBookIds, isHere);
    if (isConfigured) return saveCanonicalSelection(next);

    const disabled = supportedBookIds.filter((bookId) => !next.includes(bookId));
    return saveLegacyDenylist(legacyDisabledBooks, disabled);
  }, [
    isConfigured,
    isHere,
    legacyDisabledBooks,
    normalizeSelection,
    preferencesReady,
    rawEnabledBookIds,
    saveCanonicalSelection,
    saveLegacyDenylist,
    supportedBookIds,
  ]);

  const toggleBook = useCallback((rawBookId: string) => {
    if (!preferencesReady || (!isConfigured && legacySaving)) return;
    const bookId = normalizeSportsbookId(rawBookId);
    if (!bookId || !selectableBookIds.includes(bookId)) return;

    phCapture("filter_changed", {
      filter_name: "sportsbook",
      filter_value: bookId,
      page: "odds-screen",
    });

    const next = new Set(rawEnabledBookIds);
    if (next.has(bookId)) next.delete(bookId);
    else next.add(bookId);
    void setSelection(selectableBookIds.filter((id) => next.has(id)));
  }, [rawEnabledBookIds, isConfigured, legacySaving, preferencesReady, selectableBookIds, setSelection]);

  const setMany = useCallback((bookIds: readonly string[], enabled: boolean): Promise<boolean> => {
    const next = new Set<string>(rawEnabledBookIds);
    for (const bookId of bookIds) {
      const id = normalizeSportsbookId(bookId);
      if (!id) continue;
      if (enabled) next.add(id);
      else next.delete(id);
    }
    return setSelection([...next]);
  }, [rawEnabledBookIds, setSelection]);

  const isBookEnabled = useCallback((rawBookId: string): boolean => {
    const bookId = normalizeSportsbookId(rawBookId);
    if (!bookId) return false;
    const rawOn = isConfigured ? effectiveBookSet.has(bookId) : !legacyDisabledBooks.includes(bookId);
    return rawOn && isHere(bookId);
  }, [effectiveBookSet, isConfigured, isHere, legacyDisabledBooks]);

  const clearAll = useCallback(async (): Promise<boolean> => {
    if (!preferencesReady) return false;
    const next = keepHidden(selectableBookIds, rawEnabledBookIds, isHere);
    if (next.length < selectableBookIds.length) return setSelection(next);
    if (isConfigured) {
      const result = await actions.clearOverride(SPORTSBOOK_SURFACE.ODDS_SCREEN);
      return result.success;
    }
    return saveLegacyDenylist(legacyDisabledBooks, []);
  }, [actions, isConfigured, isHere, legacyDisabledBooks, preferencesReady, rawEnabledBookIds, saveLegacyDenylist, selectableBookIds, setSelection]);

  const selectAll = useCallback(() => {
    void setSelection(selectableBookIds);
  }, [selectableBookIds, setSelection]);

  const applySavedBooks = useCallback(() => {
    void setSelection(savedBookIds);
  }, [savedBookIds, setSelection]);

  const usingSavedBooks = useMemo(() => {
    const on = rawEnabledBookIds.filter(isHere);
    const saved = new Set(savedBookIds.filter(isHere));
    return on.length === saved.size && on.every((bookId) => saved.has(bookId));
  }, [isHere, rawEnabledBookIds, savedBookIds]);

  const disableAll = useCallback((bookIds: string[]) => {
    const disabled = new Set(
      bookIds
        .map((bookId) => normalizeSportsbookId(bookId))
        .filter((bookId): bookId is NonNullable<typeof bookId> => Boolean(bookId)),
    );
    void setSelection(rawEnabledBookIds.filter((bookId) => !disabled.has(bookId)));
  }, [rawEnabledBookIds, setSelection]);

  return useMemo(() => ({
    myBooks: disabledBooks,
    enabledBookIds,
    listedBookIds: rawEnabledBookIds,
    globalDefaultBookIds,
    selectableBookIds,
    savedBookIds,
    hasSavedBooks,
    usingSavedBooks,
    applySavedBooks,
    toggleBook,
    setMany,
    isBookEnabled,
    clearAll,
    selectAll,
    disableAll,
    setSelection,
    hasCustomSelection,
    shouldFilterBooks: isConfigured || legacyDisabledBooks.length > 0,
    isConfigured,
    isOverrideActive,
    error: meta.status === "unavailable" ? meta.error : null,
  }), [
    applySavedBooks,
    clearAll,
    disableAll,
    disabledBooks,
    enabledBookIds,
    rawEnabledBookIds,
    globalDefaultBookIds,
    hasCustomSelection,
    hasSavedBooks,
    savedBookIds,
    selectableBookIds,
    usingSavedBooks,
    isBookEnabled,
    isConfigured,
    isOverrideActive,
    legacyDisabledBooks.length,
    meta.error,
    meta.status,
    selectAll,
    setSelection,
    setMany,
    toggleBook,
  ]);
}
